import React from "react";
import { useTranslation } from "react-i18next";
import {
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Prefecture, RegionWithPrefectures } from "@quest_editor/mocks";

const PrefectureField = ({
  prefectures,
  regionsWithPrefectures,
  prefectureIds,
  setPrefectureIds,
}: {
  prefectures: Prefecture[];
  regionsWithPrefectures: RegionWithPrefectures[];
  prefectureIds: string[];
  setPrefectureIds: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const { t } = useTranslation("questEditor");

  const handleRegionChange =
    (region: RegionWithPrefectures) =>
    (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setPrefectureIds((prev: string[]) => {
        const ids = region.prefectures.map((p) => p.id);
        return checked
          ? Array.from(new Set([...prev, ...ids]))
          : prev.filter((pid) => !ids.includes(pid));
      });
    };

  const handlePrefectureChange =
    (id: string) =>
    (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      checked
        ? setPrefectureIds((prev: string[]) =>
            prev.includes(id) ? prev : [...prev, id],
          )
        : setPrefectureIds((prev: string[]) =>
            prev.filter((pid) => pid !== id),
          );
    };

  return (
    <Accordion
      sx={{
        ".MuiAccordionSummary-root .MuiSvgIcon-root": {
          transform: "rotate(0deg)",
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {prefectureIds.length > 0 ? (
          <Typography color="primary">
            {prefectures
              .filter((p) => prefectureIds.includes(p.id))
              .map((p) => p.name)
              .join(", ")}
          </Typography>
        ) : (
          <Typography color="primary">
            {t("quest_edit_target.label.not_specified.all_target")}
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="column">
          {regionsWithPrefectures.map((region: RegionWithPrefectures) => {
            const checkedCount = region.prefectures.filter((p) =>
              prefectureIds.includes(p.id),
            ).length;

            return (
              <Stack key={region.id} direction="row" alignItems="start">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkedCount === region.prefectures.length}
                      indeterminate={
                        checkedCount > 0 &&
                        checkedCount < region.prefectures.length
                      }
                      onChange={handleRegionChange(region)}
                    />
                  }
                  label={region.name}
                  sx={{
                    color: "text.secondary",
                    width: "200px",
                    flexShrink: 0,
                  }}
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    flexGrow: 1,
                    whiteSpace: "nowrap",
                    flexWrap: "wrap",
                  }}
                >
                  {region.prefectures.map((prefecture) => (
                    <FormControlLabel
                      key={prefecture.id}
                      control={
                        <Checkbox
                          checked={prefectureIds.includes(prefecture.id)}
                          onChange={handlePrefectureChange(prefecture.id)}
                        />
                      }
                      label={prefecture.name}
                      sx={{
                        color: "text.secondary",
                        display: prefecture.display ? "block" : "none",
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default PrefectureField;
